"use client";

/**
 * The second, explicit step of a restore — and a MOMENT, not a place: it is
 * registered in `GLOBAL_MODAL_IDS` (so it stacks above the history modal) but
 * deliberately NOT in `MODAL_URL_CONFIG`, because Forward could not faithfully
 * restore a half-confirmed write (MODAL_BROWSER_HISTORY).
 *
 * Non-negotiables from the plan, all enforced here:
 *  - EVERY field that will be written is listed BY NAME, current → restored.
 *    Never "restore 3 fields" without saying which.
 *  - The confirm button names the count, so the click target states its scope.
 *  - Stale fields are called out by name WITH the newer value being discarded —
 *    the one consequence the user cannot otherwise see.
 *  - Rejected fields are listed WITH their reasons.
 *  - Cancel takes the default focus. Nothing is written until the second click.
 *  - Undelete states that cascade-deleted children do not come back.
 */

import React from "react";
import { AlertTriangle, ArrowRight, ShieldAlert } from "lucide-react";
import { MODAL_IDS, ResponsiveModal } from "@wtc/ui";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { RestorePlanResponse } from "@/lib/audit/api-contract";

import { EMPTY_VALUE_TEXT } from "./AuditFieldDiff";

/** Confirm-button label. The count is part of the click target, by design. */
export function restoreConfirmLabel(plan: RestorePlanResponse | null): string {
  if (!plan) return "Restore";
  if (plan.kind === "undelete") return "Restore this record";
  const count = plan.fields.length;
  if (count === 0) return "Nothing to restore";
  return count === 1 ? "Restore 1 field" : `Restore ${count} fields`;
}

function ValueText({ display, strike = false }: { display: string | null; strike?: boolean }) {
  const isEmpty = display === null || display === "";
  return (
    <span
      className={cn(
        "break-words",
        strike && "line-through",
        isEmpty && "italic text-muted-foreground"
      )}
    >
      {isEmpty ? EMPTY_VALUE_TEXT : display}
    </span>
  );
}

export interface RestoreConfirmDialogProps {
  /** Whose history this is — "Acme Corp", or the entity label as a fallback. */
  subjectLabel: string;
  /** "5 days ago" — the event being reverted, in the same words as its row. */
  eventTimeLabel: string;
  /** Null while the plan is still being fetched. */
  plan: RestorePlanResponse | null;
  isPlanning: boolean;
  isApplying: boolean;
  /**
   * True after a 409: the record moved while the dialog was open, so the plan
   * below has been re-fetched and must be read again. Never a silent retry.
   */
  planChanged: boolean;
  /** Any other failure, already user-facing. */
  errorMessage: string | null;
  /** True when the signed-in user is not an admin (undelete is admin-only). */
  adminRequiredButMissing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RestoreConfirmDialog({
  subjectLabel,
  eventTimeLabel,
  plan,
  isPlanning,
  isApplying,
  planChanged,
  errorMessage,
  adminRequiredButMissing,
  onCancel,
  onConfirm,
}: RestoreConfirmDialogProps) {
  const isBlocked = plan?.kind === "blocked";
  const hasWritableFields = (plan?.fields.length ?? 0) > 0 || plan?.kind === "undelete";
  const confirmDisabled =
    !plan ||
    isPlanning ||
    isApplying ||
    isBlocked ||
    !hasWritableFields ||
    adminRequiredButMissing;

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.audit.restoreConfirm}
      // Open state comes from the global stack (the id is registered there, so
      // the dialog stacks above the history modal). `onOpenChange` still fires,
      // which is how an ESC / X / click-away close also clears the pending plan.
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title="Restore this change?"
      description={`${subjectLabel} · change from ${eventTimeLabel}`}
      maxWidth="lg"
    >
      <div className="space-y-4 p-4" data-testid="restore-confirm-dialog">
        {planChanged && (
          <p
            data-testid="restore-plan-changed"
            className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>
              This record changed while you were reading this, so nothing was written. The plan
              below has been refreshed — review it and confirm again.
            </span>
          </p>
        )}

        {errorMessage && (
          <p
            data-testid="restore-error"
            className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {errorMessage}
          </p>
        )}

        {isPlanning && !plan && (
          <p className="text-sm text-muted-foreground">Working out exactly what would change…</p>
        )}

        {plan?.kind === "undelete" && (
          <p
            data-testid="restore-undelete-notice"
            className="flex gap-2 rounded-md border border-border bg-muted/50 p-3 text-sm"
          >
            <ShieldAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>
              This recreates a deleted record at its original id. Records that were deleted along
              with it (its cascade-deleted children) do not come back.
              {plan.requiresAdmin && " Undelete is admin-only."}
            </span>
          </p>
        )}

        {adminRequiredButMissing && (
          <p data-testid="restore-admin-gate" className="text-sm text-destructive">
            Only an administrator can restore a deleted record.
          </p>
        )}

        {isBlocked && (
          <p data-testid="restore-blocked" className="text-sm text-destructive">
            {plan?.blockedReason ?? "This change cannot be restored."}
          </p>
        )}

        {plan && plan.fields.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium">These fields will be updated:</h3>
            <ul data-testid="restore-fields" className="space-y-1.5">
              {plan.fields.map((field) => (
                <li
                  key={field.field}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border border-border px-2 py-1.5 text-sm"
                >
                  <span className="min-w-32 font-medium">{field.label}</span>
                  <ValueText display={field.currentDisplay} strike />
                  <ArrowRight aria-hidden="true" className="size-3.5 text-muted-foreground" />
                  <span className="sr-only">becomes</span>
                  <ValueText display={field.restoredDisplay} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {plan && plan.fields.length === 0 && plan.kind === "field-revert" && !isBlocked && (
          <p className="text-sm text-muted-foreground">
            Nothing in this change can be written back — every field is either calculated or
            otherwise not restorable.
          </p>
        )}

        {plan && plan.stale.length > 0 && (
          <section className="space-y-2">
            <ul data-testid="restore-stale" className="space-y-1.5">
              {plan.stale.map((stale) => (
                <li
                  key={stale.field}
                  className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                >
                  <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  <span>
                    <span className="font-medium">{stale.label}</span> changed again since this
                    edit. Restoring will discard the newer value{" "}
                    <ValueText display={stale.discardedDisplay} />.
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {plan && plan.rejected.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium">Not restored:</h3>
            <ul data-testid="restore-rejected" className="space-y-1">
              {plan.rejected.map((rejected) => (
                <li key={rejected.field} className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{rejected.label}</span> —{" "}
                  {rejected.message}
                </li>
              ))}
            </ul>
          </section>
        )}

        {plan && plan.cautions.length > 0 && (
          <ul data-testid="restore-cautions" className="space-y-1">
            {plan.cautions.map((caution) => (
              <li key={caution} className="text-sm text-amber-700 dark:text-amber-300">
                {caution}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2 pt-1">
          {/* Cancel is the default focus: nothing is written by pressing Enter. */}
          <Button type="button" variant="outline" onClick={onCancel} disabled={isApplying} autoFocus>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="restore-confirm-button"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {isApplying ? "Restoring…" : restoreConfirmLabel(plan)}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
